import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PluginPlayground } from "ssovee-os-web-ui";
import "ssovee-os-web-ui/theme.css";

const REMOTE_PLUGINS_URL = "https://test.remote.ssovee.com/remotePlugins.js";
const SERVICE_WORKER_RELOAD_KEY = "remote-plugins-worker-reload";
let remotePluginsPromise;

async function unregisterControllingServiceWorker() {
  const serviceWorker = navigator.serviceWorker;
  const controllerScript = serviceWorker?.controller?.scriptURL;
  if (!controllerScript || sessionStorage.getItem(SERVICE_WORKER_RELOAD_KEY)) {
    return false;
  }

  const registrations = await serviceWorker.getRegistrations();
  const registration = registrations.find(
    (item) => item.active?.scriptURL === controllerScript,
  );
  if (!registration || !(await registration.unregister())) return false;

  sessionStorage.setItem(SERVICE_WORKER_RELOAD_KEY, "1");
  window.location.reload();
  return true;
}

function loadRemotePlugins() {
  if (customElements.get("finance-calculator")) return Promise.resolve();
  if (remotePluginsPromise) return remotePluginsPromise;

  remotePluginsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = REMOTE_PLUGINS_URL;
    script.async = true;
    script.onload = () => {
      if (customElements.get("finance-calculator")) {
        sessionStorage.removeItem(SERVICE_WORKER_RELOAD_KEY);
        resolve();
      } else {
        remotePluginsPromise = undefined;
        reject(new Error("The remote bundle did not register finance-calculator."));
      }
    };
    script.onerror = () => {
      remotePluginsPromise = undefined;
      unregisterControllingServiceWorker()
        .then((willReload) => {
          if (!willReload) {
            reject(
              new Error(
                "Unable to load the remote plugin bundle. Check the host CSP allows https://test.remote.ssovee.com in script-src and connect-src.",
              ),
            );
          }
        })
        .catch(() => {
          reject(new Error("Unable to load the remote plugin bundle."));
        });
    };
    document.head.appendChild(script);
  });

  return remotePluginsPromise;
}

function FinanceCalculatorRemote({ app, api }) {
  const remoteRef = useRef(null);

  useLayoutEffect(() => {
    const remote = remoteRef.current;
    if (!remote) return;
    remote.app = app;
    remote.api = api;
    remote.updateProps?.();
  }, [app, api]);

  return <finance-calculator ref={remoteRef} />;
}

const playgroundMetadata = {
  name: "FinanceDream",
  slug: "finance-calculator",
  version: "1.0.0",
  author: "SSOVEE Team",
  description:
    "Comprehensive financial calculator plugin for investment analysis, loan calculations, retirement planning, compound interest, ROI analysis, and financial projections. Calculate investments, mortgages, savings, taxes, and more with advanced financial tools.",
  icon: "./plugin-assets/Finance.png",
  entryPoint: "index.tsx",
  permissions: ["storage"],
  category: "finance",
  windowSize: {
    windowSize: { width: 700, height: 600 },
    isAppWindowResizing: true,
  },
  keywords: [
    "finance",
    "calculator",
    "investments",
    "loans",
    "retirement",
    "compound interest",
    "ROI",
    "financial planning",
    "mortgage calculator",
    "savings calculator",
    "tax calculator",
    "investment analysis",
    "financial tools",
    "personal finance",
    "financial calculator",
  ],
};

function Playground() {
  const [remoteReady, setRemoteReady] = useState(false);
  const [remoteError, setRemoteError] = useState("");

  useEffect(() => {
    let active = true;
    loadRemotePlugins()
      .then(() => {
        if (active) setRemoteReady(true);
      })
      .catch((error) => {
        if (active) setRemoteError(error.message);
      });

    return () => {
      active = false;
    };
  }, []);

  if (remoteError) {
    return (
      <main role="alert" style={{ padding: "2rem", textAlign: "center" }}>
        <p>{remoteError}</p>
        <Link to="/">Back to home</Link>
      </main>
    );
  }

  if (!remoteReady) {
    return (
      <main role="status" style={{ padding: "2rem", textAlign: "center" }}>
        Loading playground...
      </main>
    );
  }

  return (
    <div className="playground-viewport">
      <PluginPlayground backgroundImage="/ssovee-wallpaper-1.png" metadata={playgroundMetadata}>
        {({ app, api }) => <FinanceCalculatorRemote app={app} api={api} />}
      </PluginPlayground>
    </div>
  );
}

export default Playground;
